import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../../../../api/axios";

const CheckInPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [roomTypes, setRoomTypes] = useState([]);
  const [rateTypes, setRateTypes] = useState([]);

  // ✔ Allow check-in only after Save Details
  const [canShowCheckIn, setCanShowCheckIn] = useState(false);

  const [form, setForm] = useState({
    check_in: "",
    check_out: "",
    arrival_from: "",
    booking_type: "",
    booking_reference_no: "",
    purpose_of_visit: "",
    remarks: "",
    adults: 1,
    children: 0,

    room_type: "",
    room_number: "",
    rate_type: "",

    title: "",
    name: "",
    father_name: "",
    mobile_no: "",
    email: "",
    gender: "",
    occupation: "",
    dob: "",
    anniversary: "",
    nationality: "",
    is_vip: false,

    contact_address: "",
    contact_city: "",
    contact_state: "",
    contact_country: "",
    contact_zipcode: "",

    identity_type: "",
    identity_no: "",
    identity_comments: "",
    front_doc: null,
    back_doc: null,
    guest_image: null,
    front_doc_preview: "",
    back_doc_preview: "",
    guest_image_preview: "",

    advance_amount: "",
    payment_mode: "",
    advance_remarks: "",

    booking_charge: "",
    nights: "",
    sub_total: "",
    tax: "",
    total: "",
    service_charge: "",
  });

  // -------------------------
  // Load room types & rates
  // -------------------------
  useEffect(() => {
    loadRoomTypes();
    loadRateTypes();
  }, []);

  useEffect(() => {
    if (roomTypes.length > 0 && rateTypes.length > 0) {
      loadReservation();
    }
  }, [roomTypes, rateTypes]);

  const loadRoomTypes = async () => {
    const res = await api.get("/pms/room-type");
    setRoomTypes(res.data?.data || []);
  };

  const loadRateTypes = async () => {
    const res = await api.get("/pms/rate-types");
    setRateTypes(res.data?.data || []);
  };

  // -------------------------
  // Load reservation data
  // -------------------------
  const loadReservation = async () => {
    const res = await api.get(`/pms/reservations/${id}`);
    const data = res.data.data;

    const matchedRoomType =
      roomTypes.find((t) => t.id === data.room?.room_type_id)?.name || "";

    const matchedRateType =
      rateTypes.find((rt) => rt.id === data.rate_type_id)?.name || "";

    const ratePrice =
      rateTypes.find((rt) => rt.id === data.rate_type_id)?.base_price || 0;

    setForm((prev) => ({
      ...prev,
      check_in: data.check_in.substring(0, 10),
      check_out: data.check_out.substring(0, 10),
      arrival_from: data.arrival_from || "",
      booking_type: data.booking_type || "",
      booking_reference_no: data.booking_reference_no || "",
      purpose_of_visit: data.purpose_of_visit || "",
      remarks: data.remarks || "",
      adults: data.adults,
      children: data.children,

      room_type: matchedRoomType,
      room_number: data.room?.room_number,
      rate_type: matchedRateType,
      room_id: data.room?.id,
      room_price: data.room?.room_price,
      rate_price: ratePrice,

      title: data.guest?.title,
      name: data.guest?.name,
      father_name: data.guest?.father_name,
      mobile_no: data.guest?.mobile_no,
      email: data.guest?.email,
      gender: data.guest?.gender,
      occupation: data.guest?.occupation,
      dob: data.guest?.dob,
      anniversary: data.guest?.anniversary,
      nationality: data.guest?.nationality,
      is_vip: data.guest?.is_vip,

      contact_address: data.guest?.address,
      contact_city: data.guest?.city,
      contact_state: data.guest?.state,
      contact_country: data.guest?.country,
      contact_zipcode: data.guest?.zipcode,

      identity_type: data.guest?.identity_type,
      identity_no: data.guest?.identity_no,
      identity_comments: data.guest?.identity_comments,
      guest_image_preview: data.guest?.guest_image_url,
      front_doc_preview: data.guest?.front_doc_url,
      back_doc_preview: data.guest?.back_doc_url,
    }));

    // Disable check-in until user saves
    setCanShowCheckIn(false);

    setLoading(false);
  };

  // -------------------------
  // Price Calculation
  // -------------------------
  useEffect(() => {
    const calculatePrice = async () => {
      if (!form.room_id || !form.check_in || !form.check_out) return;

      const ci = new Date(form.check_in);
      const co = new Date(form.check_out);

      const nights = (co - ci) / 86400000;
      if (nights <= 0) return;

      const roomPrice = Number(form.room_price || 0);
      const ratePrice = Number(form.rate_price || 0);

      const bookingCharge = roomPrice + ratePrice;
      const subTotal = bookingCharge * nights;

      const taxRes = await api.get(`/pms/taxes`);
      const taxes = taxRes.data?.data || [];

      let taxAmount = 0;
      taxes.forEach((t) => {
        taxAmount += (subTotal * t.percentage) / 100;
      });

      const total = subTotal + taxAmount;

      updateField("booking_charge", bookingCharge.toFixed(2));
      updateField("nights", nights);
      updateField("sub_total", subTotal.toFixed(2));
      updateField("tax", taxAmount.toFixed(2));
      updateField("total", total.toFixed(2));
    };

    calculatePrice();
  }, [
    form.room_id,
    form.rate_type,
    form.check_in,
    form.check_out,
    form.service_charge,
  ]);

  // -------------------------
  // Update field helper
  // -------------------------
  const updateField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
  };

  // -------------------------
  // Save Details (updates guest)
  // -------------------------
  const saveDetails = async () => {
    try {
      const fd = new FormData();

      Object.keys(form).forEach((k) => {
        if (["front_doc", "back_doc", "guest_image"].includes(k)) return;
        if (form[k] !== null) fd.append(k, form[k]);
      });

      if (form.front_doc) fd.append("front_doc", form.front_doc);
      if (form.back_doc) fd.append("back_doc", form.back_doc);
      if (form.guest_image) fd.append("guest_image", form.guest_image);

      await api.post(`/pms/reservations/${id}/update`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      Swal.fire("Saved", "Reservation updated successfully", "success");

      // ✔ Allow check-in AFTER saving
      setCanShowCheckIn(true);

      loadReservation();
    } catch (err) {
      Swal.fire("Error", "Failed to update", "error");
    }
  };

  // -------------------------
  // Check In (identity also updates)
  // -------------------------
  const handleCheckIn = async () => {
    try {
      const fd = new FormData();

      Object.keys(form).forEach((key) => {
        // Skip file keys here (we'll append manually below)
        if (["front_doc", "back_doc", "guest_image"].includes(key)) return;

        const value = form[key];
        if (value !== undefined && value !== null) {
          fd.append(key, value);
        }
      });

      // Append files
      if (form.front_doc) fd.append("front_doc", form.front_doc);
      if (form.back_doc) fd.append("back_doc", form.back_doc);
      if (form.guest_image) fd.append("guest_image", form.guest_image);

      await api.post(`/pms/reservations/${id}/checkin`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      Swal.fire("Checked In", "Guest is now checked in", "success");

      navigate("/room-booking-list");
    } catch (err) {
      Swal.fire("Error", "Failed to check in", "error");
    }
  };

  // -------------------------
  // Combined Check-In Condition
  // -------------------------
  const canCheckIn = () => {
    const hasIdentity =
      (form.front_doc_preview || form.front_doc) &&
      (form.back_doc_preview || form.back_doc) &&
      form.identity_no &&
      form.identity_type;

    return hasIdentity;
  };

  // -------------------------
  // UI Rendering
  // -------------------------
  if (loading) return <h4>Loading...</h4>;

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <form>
        <div className="row g-3">
          <div className="col-sm-12">
            <h3 className="fw-bold mb-0">Check-In Details</h3>
          </div>

          {/* ====================== */}
          {/* RESERVATION DETAILS   */}
          {/* ====================== */}
          <div className="col-sm-12">
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="card-title">Reservation Details</h6>
              </div>

              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label text-muted">Check-In</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-calendar-plus"></i>
                      </span>
                      <input
                        type="date"
                        className="form-control"
                        value={form.check_in}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted">Check-Out</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-calendar-minus"></i>
                      </span>
                      <input
                        type="date"
                        className="form-control"
                        value={form.check_out}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted">
                      Arrival From
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-stop-circle"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={form.arrival_from}
                        onChange={(e) =>
                          updateField("arrival_from", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted">
                      Booking Type
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-house"></i>
                      </span>
                      <select
                        className="form-select"
                        value={form.booking_type}
                        disabled
                      >
                        <option value="">Select</option>
                        <option>Walk-in</option>
                        <option>Online</option>
                        <option>Corporate</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted">
                      Booking Reference No
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-bookmark-heart"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={form.booking_reference_no}
                        onChange={(e) =>
                          updateField("booking_reference_no", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted">
                      Purpose of Visit
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-eyeglasses"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={form.purpose_of_visit}
                        onChange={(e) =>
                          updateField("purpose_of_visit", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <label className="form-label text-muted">Remarks</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-bookmark-star"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={form.remarks}
                        onChange={(e) => updateField("remarks", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ====================== */}
          {/* ROOM DETAILS SECTION  */}
          {/* ====================== */}
          <div className="col-sm-12">
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="card-title">Room Details</h6>
              </div>

              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label text-muted">Room Type</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-house"></i>
                      </span>
                      <input
                        className="form-control"
                        disabled
                        value={form.room_type}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted">Room No</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-door-open"></i>
                      </span>
                      <input
                        className="form-control"
                        disabled
                        value={form.room_number}
                      />
                    </div>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted">Rate Type</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-cash"></i>
                      </span>
                      <input
                        className="form-control"
                        disabled
                        value={form.rate_type}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted">Adults</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-people"></i>
                      </span>
                      <input
                        type="number"
                        className="form-control"
                        value={form.adults}
                        onChange={(e) => updateField("adults", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted">Children</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-person"></i>
                      </span>
                      <input
                        type="number"
                        className="form-control"
                        value={form.children}
                        onChange={(e) =>
                          updateField("children", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ====================== */}
          {/* GUEST DETAILS SECTION */}
          {/* ====================== */}
          <div className="col-sm-12">
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="card-title">Customer Details</h6>
              </div>

              <div className="card-body">
                <div className="row g-4">
                  {/* Guest Section */}
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-header">
                        <h6 className="card-title">Guest Details</h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label text-muted">
                              Title
                            </label>
                            <select
                              className="form-select"
                              value={form.title}
                              onChange={(e) =>
                                updateField("title", e.target.value)
                              }
                            >
                              <option value="">Select</option>
                              <option>Mr</option>
                              <option>Ms</option>
                              <option>Mrs</option>
                            </select>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label text-muted">
                              Full Name
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="bi bi-person-circle"></i>
                              </span>
                              <input
                                className="form-control"
                                value={form.name}
                                onChange={(e) =>
                                  updateField("name", e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label text-muted">
                              Mobile No.
                            </label>
                            <div className="input-group">
                              <span className="input-group-text">
                                <i className="bi bi-phone"></i>
                              </span>
                              <input
                                className="form-control"
                                value={form.mobile_no}
                                onChange={(e) =>
                                  updateField("mobile_no", e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label text-muted">
                              Email
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              value={form.email}
                              onChange={(e) =>
                                updateField("email", e.target.value)
                              }
                            />
                          </div>

                          <div className="col-md-12">
                            <label className="form-label text-muted">
                              Gender
                            </label>
                            <div className="d-flex align-items-center gap-4 mt-2">
                              <div>
                                <input
                                  type="radio"
                                  value="Male"
                                  checked={form.gender === "male"}
                                  onChange={() => updateField("gender", "Male")}
                                />{" "}
                                Male
                              </div>

                              <div>
                                <input
                                  type="radio"
                                  value="Female"
                                  checked={form.gender === "female"}
                                  onChange={() =>
                                    updateField("gender", "Female")
                                  }
                                />{" "}
                                Female
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12">
                            <label className="form-label text-muted">
                              Customer Image
                            </label>
                            <input
                              type="file"
                              className="form-control"
                              onChange={(e) => {
                                updateField("guest_image", e.target.files[0]);
                                updateField(
                                  "guest_image_preview",
                                  URL.createObjectURL(e.target.files[0])
                                );
                              }}
                            />
                            {form.guest_image_preview && (
                              <img
                                src={form.guest_image_preview}
                                alt="Back Document"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                  border: "1px solid #ccc",
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Identity Card */}
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-header">
                        <h6 className="card-title">Identity Details</h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label text-muted">
                              ID Type
                            </label>
                            <select
                              className="form-select"
                              value={form.identity_type}
                              onChange={(e) =>
                                updateField("identity_type", e.target.value)
                              }
                            >
                              <option value="">Select</option>
                              <option>Aadhaar Card</option>
                              <option>Passport</option>
                              <option>Driving License</option>
                              <option>Voter ID</option>
                            </select>
                          </div>

                          <div className="col-md-6">
                            <label className="form-label text-muted">
                              ID Number
                            </label>
                            <input
                              className="form-control"
                              value={form.identity_no}
                              onChange={(e) =>
                                updateField("identity_no", e.target.value)
                              }
                            />
                          </div>

                          <div className="col-md-12 mt-2">
                            <label className="form-label text-muted">
                              Front Document
                            </label>
                            <input
                              type="file"
                              className="form-control"
                              onChange={(e) => {
                                updateField("front_doc", e.target.files[0]);
                                updateField(
                                  "front_doc_preview",
                                  URL.createObjectURL(e.target.files[0])
                                );
                              }}
                            />
                            {form.front_doc_preview && (
                              <img
                                src={form.front_doc_preview}
                                alt="Back Document"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                  border: "1px solid #ccc",
                                }}
                              />
                            )}
                          </div>

                          <div className="col-md-12 mt-2">
                            <label className="form-label text-muted">
                              Back Document
                            </label>
                            <input
                              type="file"
                              className="form-control"
                              onChange={(e) => {
                                updateField("back_doc", e.target.files[0]);
                                updateField(
                                  "back_doc_preview",
                                  URL.createObjectURL(e.target.files[0])
                                );
                              }}
                            />
                            {form.back_doc_preview && (
                              <img
                                src={form.back_doc_preview}
                                alt="Back Document"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                  border: "1px solid #ccc",
                                }}
                              />
                            )}
                          </div>

                          <div className="col-md-12 mt-2">
                            <label className="form-label text-muted">
                              Comments
                            </label>
                            <textarea
                              className="form-control"
                              value={form.identity_comments}
                              onChange={(e) =>
                                updateField("identity_comments", e.target.value)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ====================== */}
          {/* PAYMENT SECTION        */}
          {/* ====================== */}
          <div className="col-sm-12">
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="card-title">Advance Payment</h6>
              </div>

              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label text-muted">
                      Advance Amount
                    </label>
                    <input
                      className="form-control"
                      value={form.advance_amount}
                      onChange={(e) =>
                        updateField("advance_amount", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted">
                      Payment Mode
                    </label>
                    <select
                      className="form-select"
                      value={form.payment_mode}
                      onChange={(e) =>
                        updateField("payment_mode", e.target.value)
                      }
                    >
                      <option value="">Select</option>
                      <option>Cash</option>
                      <option>Card</option>
                      <option>UPI</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="form-label text-muted">
                      Advance Remarks
                    </label>
                    <input
                      className="form-control"
                      value={form.advance_remarks}
                      onChange={(e) =>
                        updateField("advance_remarks", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* ======================== */}
          {/* BILLING DETAILS          */}
          {/* ======================== */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title">Billing Details</h6>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0">
                    <tbody>
                      <tr>
                        <th>Booking Charge (Per Night)</th>
                        <td>{form.booking_charge || 0}</td>
                      </tr>

                      <tr>
                        <th>Nights</th>
                        <td>{form.nights || 0}</td>
                      </tr>

                      <tr>
                        <th scope="row">Sub Total</th>
                        <td>{form.sub_total || "—"}</td>
                      </tr>

                      <tr>
                        <th scope="row">Tax</th>
                        <td>{form.tax || "—"}</td>
                      </tr>

                      <tr>
                        <th scope="row">Total</th>
                        <td>
                          <span className="badge bg-primary">
                            {form.total || "—"}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* ====================== */}
          {/* BUTTONS                */}
          {/* ====================== */}
          <div className="col-sm-12 text-end">
            <button
              type="button"
              className="btn btn-primary me-2"
              onClick={saveDetails}
            >
              Save Details
            </button>

            {canCheckIn() && (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleCheckIn}
              >
                Check In
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CheckInPage;
